import "quill/dist/quill.snow.css";
import "../assets/scss/main.scss";

import Quill from "quill";
import { useEffect, useRef, useState } from "react";

type RichEditorProps = {
  inputValue: string | null | undefined;
  setInputValue: (value: string) => void;
};

// ToDo: Ideally we'd store quill deltas (which is a subset of json) in the db vs the current json that's used.
// We'd then no longer need to use jsonToHtml, htmlToJson, getSemanticHTML (instead, we'd use getContents), and clipboard.convert.

function jsonToHtml(json: string) {
  const inputJson: { body: string } = JSON.parse(json);
  const html = inputJson.body;
  return html;
}

function htmlToJson(html: string) {
  return JSON.stringify({ body: html });
}

export const RichEditor = ({ inputValue, setInputValue }: RichEditorProps) => {
  const [htmlFromEditor, setHtmlFromEditor] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const isDataInitialized = useRef(false);

  useEffect(() => {
    if (containerRef.current) {
      quillRef.current = new Quill(containerRef.current, {
        theme: "snow",
        bounds: containerRef.current,
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
          ],
        },
      });

      quillRef.current.on("text-change", () => {
        setHtmlFromEditor(quillRef.current!.getSemanticHTML());
      });

      // Add aria-labels to toolbar elements for accessibility
      const toolbar = containerRef.current.querySelector(".ql-toolbar");
      if (toolbar) {
        const labels: Record<string, string> = {
          ".ql-bold": "Bold",
          ".ql-italic": "Italic",
          ".ql-underline": "Underline",
          ".ql-link": "Insert link",
          ".ql-clean": "Remove formatting",
          ".ql-list[value='ordered']": "Ordered list",
          ".ql-list[value='bullet']": "Bullet list",
        };
        Object.entries(labels).forEach(([selector, label]) => {
          toolbar.querySelector(selector)?.setAttribute("aria-label", label);
        });
        toolbar
          .querySelector(".ql-header .ql-picker-label")
          ?.setAttribute("aria-label", "Select heading level");
      }
    }

    return () => {
      containerRef.current = null;
    };
  }, []);

  // get initial value for editor to display; inputValue may not be loaded when component is mounted
  useEffect(() => {
    if (!isDataInitialized.current && inputValue && quillRef.current) {
      // ToDo: clipboard.convert() is not in the quill documentation... ideally we wouldn't have to use it
      const delta = quillRef.current.clipboard.convert({
        html: jsonToHtml(inputValue),
      });
      quillRef.current.setContents(delta);
      isDataInitialized.current = true;
    }
  }, [inputValue]);

  useEffect(() => {
    setInputValue(htmlToJson(htmlFromEditor));
  }, [htmlFromEditor]);

  return (
    <div className="editor-wrapper">
      <div ref={containerRef}></div>
    </div>
  );
};
